import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import pwcLogo from "../../../assets/images/PwC_Outline_Logo_Black.png";
import "./AuthLayout.scss";
import { EnvironmentVariable } from "constants/EnvironmentVariables";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCallback, useEffect, useState } from "react";
import { AuthSessionStorageKeys } from "../navigation/config";
import {
  getIDAMAccessToken,
  generateCodeVerifier,
  generateCodeChallenge,
  handleAddAuthObjectToSessionStorage,
  isTokenExpired,
} from "../utils/verifyPwCAccess";
import { getBaseURL } from "utils/HTTPUtils/http-utils";
import AuthMessage from "../components/AuthMessage";
import { HomeRoutesConfig } from "features/home/navigation/config";
import { useGlobalLoader } from "components/common";
import { AppConstant } from "constants/AppConstant";

export function AuthLayout() {
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accessToken, setAccessToken] = useState(
    sessionStorage.getItem(AuthSessionStorageKeys.accessToken),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const { idamConfig } = EnvironmentVariable;
  const location = useLocation();
  const isLogoutPage = location.pathname.includes("logout");
  const { showGlobalLoader } = useGlobalLoader();
  const code = searchParams.get("code"); // null when logged out

  const initateLogin = async () => {
    const redirectURL = `${getBaseURL(idamConfig.appURL)}/auth`;
    const codeVerifier = await generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    sessionStorage.setItem(AuthSessionStorageKeys.codeVerifier, codeVerifier);
    const idamLoginURL = `${getBaseURL(idamConfig.loginURL)}client_id=${getBaseURL(idamConfig.clientId)}&redirect_uri=${redirectURL}&response_type=${idamConfig.responseType}&scope=${encodeURIComponent(getBaseURL(idamConfig.scope))}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
    window.location.replace(idamLoginURL);
  };

  const storeAuthInfoAfterLogin = useCallback(async () => {
    if (!code) return;

    setIsLoading(true);
    setAuthError(false);
    try {
      const authObject = await getIDAMAccessToken(code);
      if (!authObject?.access_token) {
        setAuthError(true);
        return;
      }
      handleAddAuthObjectToSessionStorage(authObject);
      setAccessToken(authObject?.access_token);
    } catch (error) {
      console.warn("Failed to store auth info after login:", error);
      setAuthError(true);
    } finally {
      setIsLoading(false);
    }
  }, [code]);

  useEffect(() => {
    storeAuthInfoAfterLogin().catch(console.error);
  }, [storeAuthInfoAfterLogin]);

  // If we already have a valid access token then navigate to home page
  useEffect(() => {
    if (accessToken) {
      if (!isTokenExpired(accessToken)) {
        navigate(HomeRoutesConfig.homePage);
      }
      setIsLoading(false);
    }
  }, [accessToken, navigate]);

  useEffect(() => {
    isLoading ? showGlobalLoader(true) : showGlobalLoader(false);
  }, [showGlobalLoader, isLoading]);

  return (
    <Box>
      {authError && (
        <Grid container component="main" sx={{ minHeight: "100vh" }}>
          <Grid size={{ md: 8, xs: 12 }}>
            <Box>
              <img
                src={pwcLogo as string}
                alt={t("PwCLogo")}
                className="landing-logo"
                loading="lazy"
              />
              <Box
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                sx={{ height: "100vh", marginLeft: "8vw" }}
              >
                <Box ml={12}>
                  <Typography
                    mb={3}
                    variant="h1"
                    sx={{ fontWeight: "500", fontSize: "1.5rem" }}
                  >
                    {AppConstant.appName}
                  </Typography>
                  <AuthMessage
                    message={t("loginError")}
                    buttonText={t("loginBtn")}
                    onButtonClick={initateLogin}
                    isLoading={isLoading}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 4, xs: 0 }} className="landing-image"></Grid>
        </Grid>
      )}
      {!code && !authError && (
        <Grid container component="main" sx={{ minHeight: "100vh" }}>
          <Grid size={{ md: 8, xs: 12 }}>
            <Box>
              <img
                src={pwcLogo as string}
                alt={t("PwCLogo")}
                className="landing-logo"
                loading="lazy"
              />
              <Box
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                sx={{ height: "100vh", marginLeft: "8vw" }}
              >
                <Box ml={12}>
                  <Typography
                    mb={3}
                    variant="h1"
                    sx={{ fontWeight: "500", fontSize: "1.5rem" }}
                  >
                    {AppConstant.appName}
                  </Typography>
                  {isLogoutPage ? (
                    <AuthMessage
                      message={t("logoutMessage")}
                      buttonText={t("loginBtn")}
                      onButtonClick={initateLogin}
                      isLoading={isLoading}
                    />
                  ) : (
                    <AuthMessage
                      message={t("description")}
                      buttonText={t("loginBtn")}
                      onButtonClick={initateLogin}
                      isLoading={isLoading}
                      disableButton={!!accessToken}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 4, xs: 0 }} className="landing-image"></Grid>
        </Grid>
      )}
    </Box>
  );
}
